import './ByzzerMenu.scss';
import React, {ReactNode, useRef, useMemo} from "react";
import classnames from "classnames";
import Tippy, {TippyProps} from '@tippyjs/react';
import {useNavigate, Link} from "react-router-dom";
import {Instance} from "tippy.js";
import {trackEvent} from "@/components/SiteAnalytics";
import {nanoid} from 'nanoid';

export type ByzzerMenuItem = {
    content: ReactNode;
    showIf?: boolean;
    onClick?: (item: ByzzerMenuItem) => void;
    to?: string;
    replace?: boolean;
    iconType?: string;
    disabled?: boolean;
    toolTipHelpText?: string;
    trackClick?:any,
    external?: boolean,
    target?: string; // e.g., '_blank', '_self', etc.
}

export type ByzzerMenuProps = {
    items: ByzzerMenuItem[];
    header?: ReactNode;
    footer?: ReactNode;
    trigger?: 'click' | 'hover';
    highlightItemOnHover?: boolean;
} & Partial<Omit<TippyProps, 'interactive'>>

const baseClassName = 'byzzer-menu';

export function ByzzerMenu({
    className,
    offset = [0, 0],
    trigger = 'click',
    items,
    header,
    footer,
    children,
    highlightItemOnHover,
    ...props
}: ByzzerMenuProps) {

    const navigate = useNavigate();
    const tippy = useRef<Instance | null>(null);

    function handleClick(item: ByzzerMenuItem, event?: React.MouseEvent) {
        event?.preventDefault(); // Prevent default behavior for links

        if (item.external) {
            // Open external links in a new tab with the specified target
            const target = item.target || '_blank';
            window.open(item.to, target);
        } else if (item.to) {
            // Use React Router's navigate for internal links
            navigate(item.to, { replace: item.replace });
        }

        // Call the item's onClick handler if provided
        item.onClick?.(item);

        // Track the click event if `trackClick` is defined
        if (item.trackClick) {
            trackEvent({
                type: 'click',
                data: item.trackClick,
            });
        }

        // Hide the menu
        tippy.current?.hide();
    }

    const content = useMemo(() => (
        <div className={`${baseClassName}__content`}>
            {header}
            {children ? children : items.reduce((accum, item) => {
                if (item.showIf === false) return accum;

                const itemContent = (
                    <div
                        key={nanoid()}
                        className={classnames(
                            `${baseClassName}__item`, {
                                [`${baseClassName}__item--disabled`]: item?.disabled,
                                [`${baseClassName}__item--highlight`]: highlightItemOnHover
                            })}
                        title={item?.toolTipHelpText ?? ''}
                    >
                        {item.content}
                    </div>
                );

                if (item.external) {
                    // Render external links with <a> and use window.open
                    return [
                        ...accum,
                        <a
                            href={item.to}
                            target={item.target || '_blank'}
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                e.preventDefault(); // Prevent default anchor behavior
                                window.open(item.to, item.target || '_blank');
                                item.onClick?.(item);
                                if (item.trackClick) {
                                    trackEvent({
                                        type: 'click',
                                        data: item.trackClick,
                                    });
                                }
                                tippy.current?.hide();
                            }}
                            key={nanoid()}
                            className={`${baseClassName}__link`}
                        >
                            {itemContent}
                        </a>
                    ];
                } else if (item.to) {
                    // Render internal links with React Router's <Link>
                    return [
                        ...accum,
                        <Link
                            to={item.to}
                            replace={item.replace}
                            onClick={() => {
                                item.onClick?.(item);
                                if (item.trackClick) {
                                    trackEvent({
                                        type: 'click',
                                        data: item.trackClick,
                                    });
                                }
                                tippy.current?.hide();
                            }}
                            key={nanoid()}
                            className={`${baseClassName}__link`}
                        >
                            {itemContent}
                        </Link>
                    ];
                }

                // Render items without `to` as clickable divs
                return [
                    ...accum,
                    React.cloneElement(itemContent, {
                        onClick: () => handleClick(item),
                    }),
                ];
            }, [] as ReactNode[])}
            {footer}
        </div>
    ), [items, header, footer, children, highlightItemOnHover, handleClick]);

    return <Tippy className={classnames(baseClassName, className)}
                  trigger={trigger}
                  content={content}
                  offset={offset}
                  onCreate={instance => tippy.current = instance}
                  arrow={false}
                  interactive={true}
                  placement='bottom'
                  {...props}/>

}

export default ByzzerMenu;
